import { useMemo } from "react";
import { FullScreenModal } from "@/react/components/shared/ModalsV2/FullScreenModal";
import { useIsPaywallsModalsV3 } from "@/react/hooks/useIsPaywallsModalsV3";
import type { PaywallStatus } from "@/react/types";
import { shouldDisplayWorkflowsTab } from "@circle-react/helpers/paywallHelpers";
import { tabs } from "../../constants";
import { AccessTab } from "../AccessTab";
import { CheckoutTab } from "../CheckoutTab";
import { DetailsTab } from "../DetailsTab";
import { PostPurchaseTab } from "../PostPurchaseTab";
import { PricingTab } from "../PricingTab";
import { TrackingTab } from "../TrackingTab";
import { WorkflowsTab } from "../WorkflowsTab";
import type { TabOption } from "../types";
import { Header } from "./Header";
import { ModalFooter } from "./ModalFooter";
import type { usePaywallFormContentCreateFlow } from "./hooks/usePaywallFormContentCreateFlow";
import type { usePaywallFormContentEditFlow } from "./hooks/usePaywallFormContentEditFlow";

type UsePaywallFormContentHook =
  | typeof usePaywallFormContentCreateFlow
  | typeof usePaywallFormContentEditFlow;

interface PaywallFormContentProps {
  activeTab: string;
  canChangeCurrency: boolean;
  currentPaywallStatus?: PaywallStatus;
  hasSubscribers: boolean;
  isSubmitting: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we will fix this later when we convert the files to typescript
  mutation: any;
  requiresCurrency: boolean;
  setActiveTab: (tab: string) => void;
  usePaywallFormContentHook: UsePaywallFormContentHook;
  workflowsEnabled: boolean;
}

export const PaywallFormContent = ({
  activeTab,
  setActiveTab,
  usePaywallFormContentHook,
  mutation,
  isSubmitting,
  requiresCurrency,
  canChangeCurrency,
  currentPaywallStatus,
  workflowsEnabled,
  hasSubscribers,
}: PaywallFormContentProps) => {
  const { handleTabClick, tabOptions, shouldBlockSubmitButton } =
    usePaywallFormContentHook({ activeTab, setActiveTab });

  const isPaywallsModalsV3 = useIsPaywallsModalsV3();

  const renderTab = (tab: string) => {
    const tabsToRender = {
      [tabs.DETAILS_TAB]: (
        <DetailsTab
          requiresCurrency={requiresCurrency}
          canChangeCurrency={canChangeCurrency}
        />
      ),
      [tabs.CHECKOUT_TAB]: <CheckoutTab />,
      [tabs.PRICING_TAB]: <PricingTab />,
      [tabs.ACCESS_TAB]: <AccessTab hasSubscribers={hasSubscribers} />,
      [tabs.POST_PURCHASE_TAB]: (
        <PostPurchaseTab
          shouldDisplayWorkflowsTab={shouldDisplayWorkflowsTab(
            currentPaywallStatus,
          )}
          setActiveTab={setActiveTab}
        />
      ),
      [tabs.TRACKING_TAB]: <TrackingTab />,
      [tabs.WORKFLOWS_TAB]: <WorkflowsTab />,
    };

    return tabsToRender[tab];
  };

  const filteredTabOptions = useMemo(
    () =>
      !workflowsEnabled || !shouldDisplayWorkflowsTab(currentPaywallStatus)
        ? tabOptions.filter(
            (option: TabOption) => option.label !== tabs.WORKFLOWS_TAB,
          )
        : tabOptions,
    [workflowsEnabled, currentPaywallStatus, tabOptions],
  );

  if (!isPaywallsModalsV3) {
    return (
      <>
        <Header
          tabs={filteredTabOptions}
          onClickTab={handleTabClick}
          activeTab={activeTab}
        />
        <div className="mx-auto max-w-[720px]">{renderTab(activeTab)}</div>
        <div className="border-t-primary border-t">
          <ModalFooter
            isSubmitting={isSubmitting}
            mutation={mutation}
            activeTab={activeTab}
            shouldBlockSubmitButton={shouldBlockSubmitButton}
            currentPaywallStatus={currentPaywallStatus}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <FullScreenModal.Header className="z-10">
        <Header
          tabs={filteredTabOptions}
          onClickTab={handleTabClick}
          activeTab={activeTab}
          rightContainer={
            <ModalFooter
              isSubmitting={isSubmitting}
              mutation={mutation}
              activeTab={activeTab}
              shouldBlockSubmitButton={shouldBlockSubmitButton}
              currentPaywallStatus={currentPaywallStatus}
            />
          }
        />
      </FullScreenModal.Header>
      {renderTab(activeTab)}
      <div className="border-primary sticky border-t p-5 md:py-4 lg:hidden lg:px-6">
        <ModalFooter
          isSubmitting={isSubmitting}
          mutation={mutation}
          activeTab={activeTab}
          shouldBlockSubmitButton={shouldBlockSubmitButton}
          currentPaywallStatus={currentPaywallStatus}
        />
      </div>
    </>
  );
};
